import {
	type PageQuery,
	type PagedResult,
	matchesSearch,
	resolveSample,
	simulateSampleUpload,
} from "@/core/data/sampleDataSource";
import { LOW_STOCK_LIMIT } from "@/features/consignment-vendor-product/data/stockThreshold";
import { ALL_STORES_VALUE } from "@/features/consignment-vendor-sales/data/salesPeriod";
import {
	isNewPartner,
	partnerSessionDataSource,
} from "@/features/consignment-vendor/data/partnerSessionDataSource";
import type { PartnerStore } from "@/features/consignment-vendor/data/samplePartnerFixtures";
import { resolveProductBarcode } from "../print/labelPrinter";
import {
	ACTIVE_PARTNER_CATALOG,
	type CatalogProduct,
	NEW_PARTNER_CATALOG,
} from "./productCatalogFixtures";
import { productImageUri } from "./productImage";

export const PRODUCT_STATUS = {
	Available: "tersedia",
	NeedStockIn: "perlu-stock-in",
	OutOfStock: "habis",
	Inactive: "nonaktif",
	NeverStocked: "belum-stock-in",
} as const;

export type ProductStatus =
	(typeof PRODUCT_STATUS)[keyof typeof PRODUCT_STATUS];

export const PRODUCT_LIST_TAB = {
	All: "semua",
	Available: PRODUCT_STATUS.Available,
	NeedStockIn: PRODUCT_STATUS.NeedStockIn,
	OutOfStock: PRODUCT_STATUS.OutOfStock,
	NoPermit: "belum-izin",
	Inactive: PRODUCT_STATUS.Inactive,
} as const;

export type ProductListTab =
	(typeof PRODUCT_LIST_TAB)[keyof typeof PRODUCT_LIST_TAB];

export const PRODUCT_SORT_FIELD = {
	Name: "name",
	Price: "price",
	Sold: "sold",
	Sales: "sales",
} as const;

export type ProductSortField =
	(typeof PRODUCT_SORT_FIELD)[keyof typeof PRODUCT_SORT_FIELD];

export type ProductListFilters = {
	storeId: string;
	periodDays: number;
	listTab: ProductListTab;
};

export type ProductListRow = {
	productId: string;
	productCode: string;
	productName: string;
	imageUri: string;
	productStatus: ProductStatus;
	lowestSellingPrice: number;
	highestSellingPrice: number;
	soldQuantity: number;
	salesAmount: number;
};

export type ProductListSummary = {
	salesAmount: number;
	soldQuantity: number;
	activeProductCount: number;
	totalProductCount: number;
	needStockInCount: number;
};

export type ProductListResult = PagedResult<ProductListRow> & {
	tabCounts: Record<ProductListTab, number>;
	summary: ProductListSummary;
};

export type ProductStoreRow = {
	storeId: string;
	storeName: string;
	sellingPrice: number;
	commissionPercent: number;
	stockQuantity: number;
	soldQuantity: number;
};

export type ProductPermit = {
	permitTypeName: string;
	permitNumber: string;
	permitFileCount: number;
	hasHalalCertificate: boolean;
};

export type ProductDetail = ProductListRow & {
	barcode: string;
	isFactoryBarcode: boolean;
	categoryName: string;
	subCategoryName: string;
	productTypeName: string;
	netContent: string;
	storageMethod: string;
	shelfLifeDays: number;
	productDescription: string;
	registeredDate: string;
	registrationNumber: string;
	photoCount: number;
	isLegacy: boolean;
	isPermitRequired: boolean;
	savedPermit?: ProductPermit;
	registeredPermitTypeName: string;
	registeredPermitNumber: string;
	totalStockQuantity: number;
	storeRows: ProductStoreRow[];
};

export type ProductPermitInput = {
	productCode: string;
	permitTypeName: string;
	permitNumber: string;
	permitFileCount: number;
	halalFileCount: number;
};

export type ProductDetailQuery = {
	productId: string;
	storeId: string;
	periodDays: number;
};

export type ProductDataSource = {
	listProducts: (
		query: PageQuery<ProductListFilters>,
	) => Promise<ProductListResult>;
	loadProductDetail: (query: ProductDetailQuery) => Promise<ProductDetail>;
	saveProductPermit: (permitInput: ProductPermitInput) => Promise<void>;
	uploadPermitFile: (
		file: Blob,
		onProgress: (percent: number) => void,
	) => Promise<void>;
};

const THIRTY_DAYS = 30;
const PRODUCT_NOT_FOUND_MESSAGE = "Produk tidak ditemukan.";

const savedPermitByProductCode = new Map<string, ProductPermit>();

// Sample catalog follows partner session.
export const loadSampleCatalog = () => {
	const partnerProfile = partnerSessionDataSource.loadPartnerProfile();
	const isNewPartnerAccount = isNewPartner(partnerProfile);
	return {
		partnerProfile,
		isNewPartnerAccount,
		stores: partnerProfile.stores,
		catalogProducts: isNewPartnerAccount
			? NEW_PARTNER_CATALOG
			: ACTIVE_PARTNER_CATALOG,
	};
};

const selectStoreIndexes = (stores: PartnerStore[], storeId: string) =>
	stores
		.map((store, storeIndex) => ({ store, storeIndex }))
		.filter(
			({ store }) => storeId === ALL_STORES_VALUE || store.storeId === storeId,
		)
		.map(({ storeIndex }) => storeIndex);

export const soldInPeriod = (soldPerThirtyDays: number, periodDays: number) =>
	Math.round((soldPerThirtyDays * periodDays) / THIRTY_DAYS);

export const hasPermitData = (productPermit?: ProductPermit) =>
	Boolean(productPermit?.permitNumber || productPermit?.permitFileCount);

const isPermitMissing = (catalogProduct: CatalogProduct) =>
	Boolean(catalogProduct.isPermitRequired) &&
	!hasPermitData(savedPermitByProductCode.get(catalogProduct.productCode));

const toProductListRow = (
	catalogProduct: CatalogProduct,
	storeIndexes: number[],
	periodDays: number,
): ProductListRow & { isPermitMissing: boolean } => {
	const selectedLines = storeIndexes
		.map((storeIndex) => catalogProduct.storeLines[storeIndex])
		.filter(Boolean);
	const isInactive = Boolean(catalogProduct.isInactive);
	const stockQuantity = selectedLines.reduce(
		(total, storeLine) => total + storeLine.stockQuantity,
		0,
	);
	const lowStockStoreCount = selectedLines.filter(
		(storeLine) => storeLine.stockQuantity <= LOW_STOCK_LIMIT,
	).length;
	const soldQuantities = selectedLines.map((storeLine) =>
		isInactive ? 0 : soldInPeriod(storeLine.soldPerThirtyDays, periodDays),
	);
	const sellingPrices = selectedLines.map(
		(storeLine) => storeLine.sellingPrice,
	);
	const resolveStatus = (): ProductStatus => {
		if (isInactive) return PRODUCT_STATUS.Inactive;
		if (catalogProduct.isNeverStocked && stockQuantity === 0)
			return PRODUCT_STATUS.NeverStocked;
		if (stockQuantity === 0) return PRODUCT_STATUS.OutOfStock;
		if (lowStockStoreCount > 0) return PRODUCT_STATUS.NeedStockIn;
		return PRODUCT_STATUS.Available;
	};
	return {
		productId: catalogProduct.productId,
		productCode: catalogProduct.productCode,
		productName: catalogProduct.productName,
		imageUri: productImageUri(catalogProduct.imageTone),
		productStatus: resolveStatus(),
		lowestSellingPrice: Math.min(...sellingPrices),
		highestSellingPrice: Math.max(...sellingPrices),
		soldQuantity: soldQuantities.reduce((total, sold) => total + sold, 0),
		salesAmount: soldQuantities.reduce(
			(total, sold, lineIndex) => total + sold * sellingPrices[lineIndex],
			0,
		),
		isPermitMissing: isPermitMissing(catalogProduct),
	};
};

const SORT_VALUE: Record<
	ProductSortField,
	(productRow: ProductListRow) => string | number
> = {
	name: (productRow) => productRow.productName.toLowerCase(),
	price: (productRow) => productRow.lowestSellingPrice,
	sold: (productRow) => productRow.soldQuantity,
	sales: (productRow) => productRow.salesAmount,
};

// One rule for the "Perlu stock-in" KPI and tab: low, empty, or never stocked.
const STOCK_IN_NEEDED_STATUSES: ProductStatus[] = [
	PRODUCT_STATUS.NeedStockIn,
	PRODUCT_STATUS.OutOfStock,
	PRODUCT_STATUS.NeverStocked,
];

const matchesTab = (
	productRow: ProductListRow & { isPermitMissing: boolean },
	listTab: ProductListTab,
) => {
	if (listTab === PRODUCT_LIST_TAB.All) return true;
	if (listTab === PRODUCT_LIST_TAB.NoPermit) return productRow.isPermitMissing;
	if (listTab === PRODUCT_LIST_TAB.NeedStockIn)
		return STOCK_IN_NEEDED_STATUSES.includes(productRow.productStatus);
	return productRow.productStatus === listTab;
};

const sampleProductDataSource: ProductDataSource = {
	listProducts: (query) => {
		const { catalogProducts, stores, isNewPartnerAccount } =
			loadSampleCatalog();
		const filters = query.filters ?? {
			storeId: ALL_STORES_VALUE,
			periodDays: THIRTY_DAYS,
			listTab: PRODUCT_LIST_TAB.All,
		};
		const storeIndexes = selectStoreIndexes(stores, filters.storeId);
		const allRows = catalogProducts.map((catalogProduct) =>
			toProductListRow(catalogProduct, storeIndexes, filters.periodDays),
		);
		const foundRows = allRows.filter((productRow) =>
			matchesSearch(
				query.search,
				productRow.productName,
				productRow.productCode,
			),
		);
		const tabCounts = Object.fromEntries(
			Object.values(PRODUCT_LIST_TAB).map((listTab) => [
				listTab,
				foundRows.filter((productRow) => matchesTab(productRow, listTab))
					.length,
			]),
		) as Record<ProductListTab, number>;
		const sortField = (query.sort?.field ??
			PRODUCT_SORT_FIELD.Sold) as ProductSortField;
		const sortSign = query.sort?.direction === "asc" ? 1 : -1;
		const sortedRows = foundRows
			.filter((productRow) => matchesTab(productRow, filters.listTab))
			.sort((leftRow, rightRow) => {
				const leftValue = SORT_VALUE[sortField](leftRow);
				const rightValue = SORT_VALUE[sortField](rightRow);
				if (leftValue === rightValue) return 0;
				return (leftValue < rightValue ? -1 : 1) * sortSign;
			});
		const activeRows = allRows.filter(
			(productRow) => productRow.productStatus !== PRODUCT_STATUS.Inactive,
		);
		const pageStart = (query.page - 1) * query.pageSize;
		const result: ProductListResult = {
			items: sortedRows.slice(pageStart, pageStart + query.pageSize),
			total: sortedRows.length,
			page: query.page,
			pageSize: query.pageSize,
			tabCounts,
			summary: {
				salesAmount: activeRows.reduce(
					(total, productRow) => total + productRow.salesAmount,
					0,
				),
				soldQuantity: activeRows.reduce(
					(total, productRow) => total + productRow.soldQuantity,
					0,
				),
				activeProductCount: isNewPartnerAccount ? 0 : activeRows.length,
				totalProductCount: allRows.length,
				needStockInCount: allRows.filter((productRow) =>
					matchesTab(productRow, PRODUCT_LIST_TAB.NeedStockIn),
				).length,
			},
		};
		return resolveSample(result, {
			...result,
			items: [],
			total: 0,
			tabCounts: Object.fromEntries(
				Object.values(PRODUCT_LIST_TAB).map((listTab) => [listTab, 0]),
			) as Record<ProductListTab, number>,
		});
	},
	loadProductDetail: async (detailQuery) => {
		const { catalogProducts, stores } = loadSampleCatalog();
		const catalogProduct = catalogProducts.find(
			(candidate) => candidate.productId === detailQuery.productId,
		);
		if (!catalogProduct) throw new Error(PRODUCT_NOT_FOUND_MESSAGE);
		const storeIndexes = selectStoreIndexes(stores, detailQuery.storeId);
		const productRow = toProductListRow(
			catalogProduct,
			storeIndexes,
			detailQuery.periodDays,
		);
		const barcode = resolveProductBarcode(
			catalogProduct.productCode,
			catalogProduct.factoryBarcode,
		);
		const productDetail: ProductDetail = {
			...productRow,
			barcode,
			isFactoryBarcode: Boolean(catalogProduct.factoryBarcode),
			categoryName: catalogProduct.categoryName,
			subCategoryName: catalogProduct.subCategoryName,
			productTypeName: catalogProduct.productTypeName,
			netContent: catalogProduct.netContent,
			storageMethod: catalogProduct.storageMethod,
			shelfLifeDays: catalogProduct.shelfLifeDays,
			productDescription: catalogProduct.productDescription,
			registeredDate: catalogProduct.registeredDate,
			registrationNumber: catalogProduct.registrationNumber,
			photoCount: catalogProduct.photoCount,
			isLegacy: Boolean(catalogProduct.isLegacy),
			isPermitRequired: Boolean(catalogProduct.isPermitRequired),
			savedPermit: savedPermitByProductCode.get(catalogProduct.productCode),
			registeredPermitTypeName: catalogProduct.permitTypeName,
			registeredPermitNumber: catalogProduct.permitNumber,
			totalStockQuantity: storeIndexes.reduce(
				(total, storeIndex) =>
					total + (catalogProduct.storeLines[storeIndex]?.stockQuantity ?? 0),
				0,
			),
			storeRows: stores.map((store, storeIndex) => {
				const storeLine = catalogProduct.storeLines[storeIndex];
				return {
					storeId: store.storeId,
					storeName: store.storeName,
					sellingPrice: storeLine?.sellingPrice ?? 0,
					commissionPercent: storeLine?.commissionPercent ?? 0,
					stockQuantity: storeLine?.stockQuantity ?? 0,
					soldQuantity: catalogProduct.isInactive
						? 0
						: soldInPeriod(
								storeLine?.soldPerThirtyDays ?? 0,
								detailQuery.periodDays,
							),
				};
			}),
		};
		return resolveSample(productDetail, productDetail);
	},
	uploadPermitFile: simulateSampleUpload,
	saveProductPermit: async (permitInput) => {
		await resolveSample(undefined, undefined);
		const existingPermit = savedPermitByProductCode.get(
			permitInput.productCode,
		);
		const hasPermitData =
			Boolean(permitInput.permitNumber) || permitInput.permitFileCount > 0;
		savedPermitByProductCode.set(permitInput.productCode, {
			permitTypeName: hasPermitData
				? permitInput.permitTypeName
				: (existingPermit?.permitTypeName ?? ""),
			permitNumber: hasPermitData
				? permitInput.permitNumber
				: (existingPermit?.permitNumber ?? ""),
			permitFileCount: hasPermitData
				? permitInput.permitFileCount
				: (existingPermit?.permitFileCount ?? 0),
			hasHalalCertificate:
				permitInput.halalFileCount > 0 ||
				Boolean(existingPermit?.hasHalalCertificate),
		});
	},
};

export const productDataSource = sampleProductDataSource;
